import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { cn } from '@/lib/utils'
import { NAV_ITEMS } from '@/components/app/nav-items'
import { useNavCounts } from '@/components/app/use-nav-counts'

export const BottomNav: FC = (): ReactNode => {
    const { pathname } = useRouter()
    const counts = useNavCounts()

    return (
        <nav
            aria-label='Main'
            className='fixed inset-x-0 bottom-0 z-40 border-t border-border bg-white/95 pb-[env(safe-area-inset-bottom)] shadow-[0_-4px_16px_-8px_rgb(14_27_48/0.10)] backdrop-blur-md lg:hidden print:hidden'
        >
            <ul className='mx-auto grid h-16 max-w-xl grid-cols-5'>
                {NAV_ITEMS.map(item => {
                    const active = item.isActive(pathname)
                    const badge = counts[item.href]
                    const count = badge?.count ?? 0
                    const Icon = item.icon

                    return (
                        <li key={item.href}>
                            <Link
                                href={item.href}
                                aria-current={active ? 'page' : undefined}
                                className={cn(
                                    'relative flex h-full flex-col items-center justify-center gap-1 text-[11px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                                    active ? 'text-primary' : 'text-slate-500 hover:text-foreground'
                                )}
                            >
                                {active && <span className='absolute top-0 h-[3px] w-10 rounded-b-full bg-primary' aria-hidden='true' />}

                                <span className='relative'>
                                    <Icon className='size-[22px]' strokeWidth={active ? 2.4 : 2} aria-hidden='true' />

                                    {count > 0 && (
                                        <span className='absolute -top-1.5 -right-2.5 min-w-[18px] rounded-full bg-orange-500 px-1 text-center text-[10px] leading-[18px] font-bold text-white ring-2 ring-white tabular-nums'>
                                            {count > 99 ? '99+' : count}
                                            <span className='sr-only'>{` ${badge?.label ?? ''}`}</span>
                                        </span>
                                    )}
                                </span>

                                {item.label}
                            </Link>
                        </li>
                    )
                })}
            </ul>
        </nav>
    )
}
